import clsx from "clsx";
import React, { FC, useEffect, useState } from "react";
import { format } from "date-fns";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";

import ProofDocumentControl from "~/components/Forms/ViolationRecordingForm/ProofDocumentControl";
import IconDownload from "~/components/Icons/icon-download";
import IconTrash from "~/components/Icons/icon-trash";
import IconUpdate from "~/components/Icons/icon-update";
import Button from "~/shared/ui/Fields/Button";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useZD } from "~/entity/ZD/hooks/useZD";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { uploadFile } from "~/libs/uploadFile";
import {
  useAddNotificationMutation,
  useSetAddValuesNotesMutation,
  useSetAddValuesPersonalMutation,
  useSetDealerFixPlanAddDocumentMutation,
  useSetDealerFixPlanRemoveDocumentDocumentMutation,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { Field } from "~/shared/ui/Field";

import s from "./UsersForm.less";

interface IProps {
  setCloseModal: () => void;
  Files?: any[];
  isResolved?: boolean;
  refetch;
  selectedService?;
  isEdit?: boolean;
  isPlanReport?: boolean;
  instance?: any;
}

const PlanReportFilesForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  selectedService,
  Files,
  isResolved = false,
  isEdit = false,
  isPlanReport = true,
  instance,
}) => {
  const [currentFiles, setCurrentFiles] = useState(Files);
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const { t } = useTranslation();
  const currentDealerId = useCurrentDealerId();
  const setData = useSetLocationData();

  const [fileId, setFileId] = useState([]);
  // const isDealer = role === "dealer" || role === "holding";
  const isDealer = hasPermission("zd.planReports.set");
  const isView = hasPermission("zd.planReports.get");
  const [filesPlan, setFilesPlan] = useState();
  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const [filesOnDelete, setFilesOnDelete] = useState([]);
  const { UserId, dateBegin, fullname, dateEnd, openModal, event, currentId } =
    useGetLocationData();

  const { PlanReportId } = useURLParamsGetter<{ PlanReportId: string }>();
  const { handleSubmit, control, errors, reset, watch } = useForm({});

  // Синхронизируем currentFiles с Files при обновлении props
  useEffect(() => {
    setCurrentFiles(Files);
  }, [Files]);

  const isFiles = fileId.length > 0;

  const [addNotificationMutation] = useAddNotificationMutation({});

  const [dealerFixPlanAddDocument] = useSetDealerFixPlanAddDocumentMutation({
    onCompleted() {
      setData("openModal", "", "currentId", "");
      refetch();
    },
  });

  const { updateFiles } = useZD();
  const { mutate: updateFilesMutation } = useMutation(updateFiles, {
    onSuccess: () => {
      refetch();
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("common.сhange_was_successful"),
        },
      });
      setCloseModal();
    },
    onError: (error: any) => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: error?.message || t("common.data_uploaded_error"),
        },
      });
    },
  });

  const [addValuesNotes] = useSetAddValuesNotesMutation({
    onCompleted() {
      setData(
        "openModal",
        "",
        "currentId",
        "",
        "sortField",
        "",
        "sortSubField",
        ""
      );
      refetch();
    },
    onError({ graphQLErrors }) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: graphQLErrors[0].message,
        },
      });
    },
  });

  const [addValuesPersonal] = useSetAddValuesPersonalMutation({
    onCompleted() {
      setData(
        "openModal",
        "",
        "currentId",
        "",
        "sortField",
        "",
        "sortSubField",
        ""
      );
      refetch();
    },
    onError({ graphQLErrors }) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: graphQLErrors[0].message,
        },
      });
    },
  });

  const [dealerFixPlanRemoveDocument] =
    useSetDealerFixPlanRemoveDocumentDocumentMutation({
      onCompleted() {
        refetch();
      },
    });

  const onSubmit = () => {
    const currentFilesIds = (currentFiles ?? []).map((el) => el.id);
    const updatedFiles = [...currentFilesIds, ...fileId].filter(
      (id) => !filesOnDelete.includes(id)
    );
    if (selectedService === "personal") {
      updateFilesMutation({
        zdPersonalId: instance?.ZD_UUID ?? instance?.zdPersonalId ?? currentId,
        fileIds: updatedFiles,
        operation: "replace",
      });
      return;
    } else if (selectedService === "notes" && !isPlanReport) {
      addValuesNotes({
        variables: {
          DealerId: currentDealerId,
          UUID: instance?.UUID || instance?.ZD_UUID || currentId,
          Image: updatedFiles,
        },
      });
    } else {
      if (filesOnDelete.length) {
        deleteFilesOnSubmit(filesOnDelete);
        setFilesOnDelete([]);
        setCloseModal();
      }
      if (isFiles) {
        dealerFixPlanAddDocument({
          variables: {
            PlanReportId: PlanReportId,
            DealerId: currentDealerId,
            fileIds: isFiles ? fileId : [],
          },
        });
      }
    }
  };
  const selectToDelete = (id) => {
    setFilesOnDelete((p) => {
      if (p.includes(id)) {
        return p.filter((el) => id !== el);
      } else {
        return [...p, id];
      }
    });
  };

  const deleteFilesOnSubmit = (arr) => {
    if (!arr.length) {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("common.files_deleted_successfully"),
        },
      });
      return;
    }
    if (selectedService === "personal") {
      const updatedFiles = (currentFiles ?? [])
        .map((el) => el.id)
        .filter((id) => !arr.includes(id));
      updateFilesMutation({
        zdPersonalId: instance?.zdPersonalId ?? currentId,
        fileIds: updatedFiles,
        operation: "replace",
      });
      return;
    }
    const resultArr = [...arr];
    const id = resultArr.pop();
    dealerFixPlanRemoveDocument({
      variables: {
        PlanReportId: PlanReportId,
        DealerId: currentDealerId,
        fileId: id,
      },
    });
    deleteFilesOnSubmit(resultArr);
  };

  const fallBack = () => {
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: t("common.data_uploaded_error"),
      },
    });
  };
  const setParams = useURLParamsSetter();

  const uploadFileProcess = async (file: File) => {
    setCurrentFiles([file]);
    const uploadedFile = await uploadFile(file, fallBack);
    if (uploadedFile) {
      setParams({ fileId: uploadedFile.id });
      setFileId((old) => [...old, uploadedFile.id]);
    }
  };

  const getReadonlyPersonalFiles = (instance: any = {}) => {
    const files = [];
    if (instance.dataConsent) {
      files.push(instance.dataConsent);
    }
    if (instance.photo) {
      files.push(instance.photo);
    }
    if (instance.positionOrder) {
      files.push(instance.positionOrder);
    }
    if (instance.jobDescription) {
      files.push(instance.jobDescription);
    }
    return files;
  };

  const readonlyPersonalFiles = getReadonlyPersonalFiles(instance);

  // TODO: логика по редактированию и удалению, в будущем потребуется уточнение
  const hasPermissionToAction = (selectedService === 'personal' && hasPermission('zd.personalEdit') || selectedService === 'notes' && hasPermission('zd.notesEdit') || selectedService === 'services' && hasPermission('zd.servicesEdit'));
  const canDelete = (hasPermissionToAction || selectedService !== 'personal' && selectedService !== 'notes' && selectedService !== 'services' && isDealer && !isResolved && !isView)
  const canEdit = (hasPermissionToAction || (selectedService !== 'personal' && isDealer))


  return (
    <div className={s.ind_form}>
      <ul className={s.list}>
        <form className={s.form_comment} onSubmit={handleSubmit(onSubmit)}>
          <div className={s.form_wrapper}>
            <div className={s.line} />

            {!!readonlyPersonalFiles.length && (
              <div className={s.wrapper_files}>
                <div className={s.title_files}>
                  <span style={{ width: 221 }}>
                    {t("planReport.planReportPersonFiles")}
                  </span>
                  <span>{t("planReport.planReportUploadTime")}</span>
                </div>
                <div className={s.items_comments}>
                  {readonlyPersonalFiles.map(
                    ({ name, link, id, createdAt }) => (
                      <div
                        className={clsx(
                          filesOnDelete.includes(id) && s.onDelete,
                          s.wrapper,
                          s.wrapperPlan
                        )}
                      >
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={clsx(
                            s.filePreview,
                            s.filePreviewPlan,
                            s.filePreviewLink
                          )}
                          style={{ maxWidth: 250 }}
                        >
                          {name}
                        </a>
                        <div className={clsx(s.filePreview, s.filePreviewPlan)}>
                          {createdAt
                            ? format(new Date(createdAt), "dd.MM.yyyy")
                            : ""}
                        </div>
                        <div
                          className={clsx(s.fileControls, s.fileControlsPlan)}
                        >
                          <a href={link} download className={s.customBtn}>
                            <IconDownload fill={"#878E94"} />
                          </a>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {currentFiles && (
              <div className={s.wrapper_files}>
                <div className={s.title_files}>
                  <span>{t("planReport.planReportScan")}</span>
                  <span>{t("planReport.planReportUploadTime")}</span>
                </div>
                <div className={s.items_comments}>
                  {currentFiles.map(({ name, link, id, createdAt }) => (
                    <div
                      className={clsx(
                        filesOnDelete.includes(id) && s.onDelete,
                        s.wrapper,
                        s.wrapperPlan
                      )}
                    >
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={clsx(
                          s.filePreview,
                          s.filePreviewPlan,
                          s.filePreviewLink
                        )}
                      >
                        {name}
                      </a>
                      <div className={clsx(s.filePreview, s.filePreviewPlan)}>
                        {createdAt
                          ? format(new Date(createdAt), "dd.MM.yyyy")
                          : ""}
                      </div>
                      <div className={clsx(s.fileControls, s.fileControlsPlan)}>
                        <button
                          onClick={() => refetch()}
                          className={s.customBtn}
                        >
                          <IconUpdate fill={"#878E94"} />
                        </button>
                        <a href={link} download className={s.customBtn}>
                          <IconDownload fill={"#878E94"} />
                        </a>
                        {canDelete && (
                          <button
                            type={"button"}
                            className={s.customBtn}
                            onClick={() => selectToDelete(id)}
                          >
                            <IconTrash fill={"#878E94"} />
                          </button>
                        )}
                      </div>
                      {isEdit && (
                        <div className={s.editButton}>
                          <Field
                            type="file"
                            name={`fileId_${id}`}
                            control={control}
                            className={s.fileField}
                            onUpload={uploadFileProcess}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={s.line} />

          </div>
          {canEdit && (
            <div>
              <div className={s.drop_zone}>
                <Field
                  type="file"
                  name="file"
                  control={control}
                  error={errors["file"]?.message}
                  accept={[
                    "image/png",
                    "image/jpeg",
                    "application/pdf",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  ]}
                  loading={uploadFileLoader}
                  className={s.fileField}
                  onUpload={async (file) => {
                    setUploadFileLoader(true);
                    try {
                      const uploaded = await uploadFile(file, fallBack);
                      if (uploaded) {
                        // Файлов может быть несколько: id уходит в общий список,
                        // а onUpload ничего не возвращает — значение поля
                        // остаётся null, и FileField снова показывает кнопку
                        // загрузки.
                        setFileId((old) => [...old, uploaded.id]);
                        addNotificationMutation({
                          variables: {
                            type: NOTIFICATIONS.success,
                            content: t("common.file_uploaded_successfully"),
                          },
                        });
                      }
                    } finally {
                      setUploadFileLoader(false);
                    }
                  }}
                />
              </div>
            </div>
          )}
          {canEdit && (
            <>
              {isFiles && (
                <div className={s.wrapper_files}>
                  <div className={s.title_files}>{t("zd.uploaded_files")}</div>
                  <div className={s.items_comments}>
                    {fileId.map((id) => (
                      <ProofDocumentControl
                        isPlanReport={true}
                        filesId={fileId}
                        setFilesId={setFileId}
                        id={id}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(!!filesOnDelete.length || isFiles) && (
                <Button
                  txtBtn={t("ui.save")}
                  isForm
                  customClassName={s.btnIcon}
                  orange
                />
              )}
            </>
          )}
        </form>
      </ul>
    </div>
  );
};
export default PlanReportFilesForm;
